import type { Placement } from '@floating-ui/react-dom';
import classNames from 'classnames';
import React, { useState } from 'react';
import * as styles from './HomeActivitiesButton.module.scss';
import useAppSelector, { useAppDispatch } from '../app/redux/useAppSelector';
import Button from '../components/Button';
import Caret from '../components/Caret';
import CheckRadioControl from '../components/formInternals/CheckRadioControl';
import FormField from '../components/formInternals/FormField';
import PopOverPrimitive from '../components/PopOverPrimitive';
import SVGIcon from '../components/SVGIcon';
import { TopicActivityAction, UserType } from '../graphql/generated/graphql';
import allTopicsIcon from '../icons/non-prefilled/16x16/allTopics.svg';
import { UIActions } from '../ui/uiSlice';
import { isFeatureEnabled } from '../utils/features';

const topicOptions = [
  {
    label: 'My topics',
    value: 'my',
  },
  {
    label: 'All topics',
    value: 'all',
  },
] as const;

const activitiesOptions = [
  {
    label: 'Post edits',
    actions: [TopicActivityAction.Edit],
  },
  {
    label: 'Post publications',
    actions: [TopicActivityAction.Publish],
  },
  {
    label: 'Post verifications',
    actions: [TopicActivityAction.Verify],
  },
  {
    label: 'Comment & Replies',
    actions: [TopicActivityAction.Comment, TopicActivityAction.Reply],
  },
  {
    label: 'Thanks',
    actions: [TopicActivityAction.Thank],
  },
];

interface HomeActivitiesButtonProps {
  popoverPlacement: Placement;
}

const HomeActivitiesButton = ({
  popoverPlacement,
}: HomeActivitiesButtonProps) => {
  const dispatch = useAppDispatch();
  const homeActivitiesFilter = useAppSelector(
    (state) => state.ui.homeActivitiesFilter,
  );
  const [isOpen, setOpen] = useState(false);
  const isGuest = useAppSelector(
    (state) => state.session.user.type === UserType.Guest,
  );

  if (!isFeatureEnabled('home_activity_v2') || isGuest) {
    return null;
  }

  return (
    <PopOverPrimitive
      onOpenChange={setOpen}
      open={isOpen}
      offset={8}
      placement={popoverPlacement}
      trigger="click"
      noFlip
    >
      <PopOverPrimitive.Trigger asChild>
        <Button
          active={isOpen}
          icon={<SVGIcon>{allTopicsIcon}</SVGIcon>}
          className={classNames(styles.trigger, { [styles.isOpen]: isOpen })}
        >
          {topicOptions.find(
            (option) => option.value === homeActivitiesFilter.topics,
          )?.label || 'Activities'}
          <span className={styles.caret}>
            <Caret direction={isOpen ? 'up' : 'down'} size={10} />
          </span>
        </Button>
      </PopOverPrimitive.Trigger>
      <PopOverPrimitive.Floating noPortal>
        <PopOverPrimitive.Content className={styles.inner}>
          <h5 className={styles.sectionTitle}>Filter by</h5>
          {topicOptions.map(({ label, value }) => (
            <CheckRadioControl
              key={value}
              type="checkbox"
              label={<span className={styles.option}>{label}</span>}
              value={value}
              checked={homeActivitiesFilter.topics === value}
              onChange={() => {
                dispatch(UIActions.setHomeActivitiesTopics(value));
              }}
            />
          ))}
          <h5 className={styles.sectionTitle}>Activity type</h5>
          {activitiesOptions.map(({ label, actions }) => (
            <FormField
              type="checkbox"
              margin="none"
              checked={
                !actions.some(
                  (action) => !homeActivitiesFilter.actions.includes(action),
                )
              }
              onChange={(event) =>
                dispatch(
                  UIActions.toggleHomeActivitiesActions({
                    actions,
                    isEnabled: event.target.checked,
                  }),
                )
              }
              value={label}
              label={<span className={styles.option}>{label}</span>}
              key={label}
            />
          ))}
        </PopOverPrimitive.Content>
      </PopOverPrimitive.Floating>
    </PopOverPrimitive>
  );
};

export default HomeActivitiesButton;
